/**
 * The viewport the software keyboard leaves you.
 *
 * `dvh` is the *layout* viewport, and on iOS the keyboard does not shrink that —
 * it shrinks the *visual* viewport and scrolls the page under it. So a pane
 * sized to `100dvh` keeps its full height when the keyboard opens, and anything
 * pinned to its bottom edge (the chat composer, in practice) ends up behind the
 * keys.
 *
 * This publishes the real number as `--mx-viewport-h`, and flags the keyboard as
 * `data-keyboard="open"` on the root element so chrome that has no business
 * sitting on top of a keyboard — the tab bar — can get out of the way.
 *
 * Called once, at the app root. Falls back to `100dvh` where `visualViewport` is
 * missing, which is the same behaviour as before this existed.
 */

import { useEffect } from 'react'

/** Below this much of the layout height, assume a keyboard rather than a URL bar. */
const KEYBOARD_RATIO = 0.75

export function useViewportHeight(): void {
  useEffect(() => {
    const viewport = window.visualViewport
    if (!viewport) return

    const root = document.documentElement
    let frame = 0

    const apply = () => {
      frame = 0
      root.style.setProperty('--mx-viewport-h', `${viewport.height}px`)

      // `innerHeight` is the layout viewport, which the keyboard leaves alone —
      // the ratio between the two is what tells them apart. A collapsing URL bar
      // moves it by a tenth or so; a keyboard takes closer to half.
      const keyboardUp = viewport.height / window.innerHeight < KEYBOARD_RATIO
      if (keyboardUp) root.setAttribute('data-keyboard', 'open')
      else root.removeAttribute('data-keyboard')
    }

    // resize and scroll both fire in bursts while the keyboard animates.
    const schedule = () => {
      if (frame) return
      frame = requestAnimationFrame(apply)
    }

    apply()
    viewport.addEventListener('resize', schedule)
    viewport.addEventListener('scroll', schedule)

    return () => {
      if (frame) cancelAnimationFrame(frame)
      viewport.removeEventListener('resize', schedule)
      viewport.removeEventListener('scroll', schedule)
      root.style.removeProperty('--mx-viewport-h')
      root.removeAttribute('data-keyboard')
    }
  }, [])
}
